import { canManageListing, getListingMember } from 'app/lib/guardian';
import { useNavigation } from 'app/hooks/navigation';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useRowSelection } from 'app/hooks/use-listing-row-card-selection';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { useFreshListingCounters } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import type { Listing } from 'app/types/listing';
import { ListingType } from 'app/types/listing';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { View } from 'react-native';
import { useIsGuestWeb } from 'app/utils/web-helpers';

interface UseListingCardOptions {
  listing: Listing;
  isFirst?: boolean;
  isSelecting?: boolean;
  selectedRowsState?: [Listing['id'][], Dispatch<SetStateAction<Listing['id'][]>>];
  tutorialViewRef?: RefObject<View | null>;
  continueTutorial?: () => Promise<boolean>;
}

function resolveListingCardOptions(options: UseListingCardOptions) {
  const {
    isFirst = (options.listing as Listing & { isFirst?: boolean }).isFirst,
    isSelecting = (options.listing as Listing & { isSelecting?: boolean }).isSelecting,
    selectedRowsState = (
      options.listing as Listing & {
        selectedRowsState?: [Listing['id'][], Dispatch<SetStateAction<Listing['id'][]>>];
      }
    ).selectedRowsState,
    tutorialViewRef,
    continueTutorial,
  } = options;

  return { isFirst, isSelecting, selectedRowsState, tutorialViewRef, continueTutorial };
}

function warnAboutLegacyListingOptions(options: UseListingCardOptions) {
  const { listing } = options;
  // TODO: Remove legacy fallbacks that read isFirst / isSelecting / selectedRowsState from `listing`; require explicit
  // UseListingCardOptions once every card caller is migrated (tracked: listing card props cleanup).
  if (
    ('isFirst' in listing && options.isFirst === undefined) ||
    ('isSelecting' in listing && options.isSelecting === undefined) ||
    ('selectedRowsState' in listing && options.selectedRowsState === undefined)
  ) {
    console.warn(
      '[useListingCard] listing carries isFirst / isSelecting / selectedRowsState; pass them via useListingCard options instead.',
      listing.id,
    );
  }
}

export function useListingCard(options: UseListingCardOptions) {
  const { listing } = options;

  if (__DEV__) warnAboutLegacyListingOptions(options);

  const { isFirst, isSelecting, selectedRowsState, tutorialViewRef, continueTutorial } =
    resolveListingCardOptions(options);

  const { id, type, user, addresses, groups, grouping, picture, pictures, numViews, numFavourites } = listing;

  const { data: myUser } = useUser();
  const navigation = useNavigation();
  const isGuestWeb = useIsGuestWeb();

  const isAuthor = canManageListing(listing, myUser?.id);
  const isInvisible = isAuthor && !addresses?.length && !groups?.length;
  const isGroupedListing = Boolean(!isAuthor && grouping);

  const { isSelected, onSelectRow } = useRowSelection(id, selectedRowsState);

  const freshCounters = useFreshListingCounters(id);
  const displayNumViews = freshCounters?.numViews ?? numViews;
  const displayNumFavourites = freshCounters?.numFavourites ?? numFavourites;
  const displayTrustPoints = listing.guardianId
    ? getListingMember(listing).trustPoints
    : (freshCounters?.trustPoints ?? user.trustPoints);
  const thumbPicture = getImageVariantUrl(pictures?.[0]?.url ?? picture, 'mini');

  const goToListing = async () => {
    if (isFirst && type === ListingType.offer && continueTutorial) {
      let shouldStopForTutorial = false;

      try {
        shouldStopForTutorial = await continueTutorial();
      } catch {
        shouldStopForTutorial = false;
      }

      if (shouldStopForTutorial) {
        return;
      }
    }
    if (isSelecting) {
      onSelectRow();
      return;
    }
    navigateToListing(navigation, id, isGroupedListing);
  };

  const withAuthorize = useAuthorize();

  const onPress = () => (isGuestWeb ? void goToListing() : withAuthorize(goToListing));

  const loanFeeAvailable = isFeatureEnabled(FeatureFlag.loanFee, myUser?.featureFlags);

  return {
    myUser,
    navigation,
    isAuthor,
    isInvisible,
    isGroupedListing,
    tutorialViewRef,
    isSelected,
    onSelectRow,
    freshCounters,
    displayNumViews,
    displayNumFavourites,
    displayTrustPoints,
    thumbPicture,
    onPress,
    loanFeeAvailable,
  };
}
